import { PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangleIcon, NewspaperIcon, PinIcon } from "lucide-react";

import { preventBubble } from "@/components/canvas-editor/utils";
import { Page } from "@/components/Page";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button, ButtonLink } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { H1, P } from "@/components/ui/typography";
import {
  adoptStickySet,
  deleteStickySet,
  getStickySetsQueryOptions,
  StickySetSummary,
  stickySetSuggestionsQueryOptions,
} from "@/lib/api";

export const Route = createFileRoute("/app/_sidebar-layout/sticky-sets/")({
  component: StickySetsPage,
  loader: ({ context: { queryClient } }) => {
    void queryClient.prefetchQuery(getStickySetsQueryOptions());
  },
  head: () => ({ meta: [{ title: "Sticky Sets" }] }),
});

const MIN_FEEDS_FOR_SUGGESTION = 5;

function DeleteSetButton({ set }: { set: StickySetSummary }) {
  const mutation = useMutation({ mutationFn: () => deleteStickySet(set.id) });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="icon">
          <span className="sr-only">Delete set</span>
          <TrashIcon />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete &ldquo;{set.display_name}&rdquo;?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This removes its {set.member_count}{" "}
            {set.member_count === 1 ? "post" : "posts"} from all{" "}
            {set.feed_count} {set.feed_count === 1 ? "feed" : "feeds"}. Posts
            that were already on a feed before you added them to this set stay
            put &mdash; only the copies this set created are removed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={(e) => {
              e.preventDefault();
              mutation.mutate();
            }}
            isLoading={mutation.isPending}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
        {mutation.error && (
          <div className="text-red-normal text-sm">
            {mutation.error.message}
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * The adoption offer.
 *
 * Most people who need sticky sets have already built them by hand, one feed at
 * a time. Rather than asking them to start over, this finds posts already on
 * several of their feeds and turns them into a set in place -- the existing
 * attachments are claimed, not re-created, so every post keeps its stats.
 */
function AdoptBanner() {
  const navigate = useNavigate();
  const { data: suggestions } = useQuery(
    stickySetSuggestionsQueryOptions(MIN_FEEDS_FOR_SUGGESTION),
  );
  const adopt = useMutation({
    mutationFn: (displayName: string) =>
      adoptStickySet({
        display_name: displayName,
        min_feeds: MIN_FEEDS_FOR_SUGGESTION,
      }),
    onSuccess: (set) => {
      navigate({
        to: "/app/sticky-sets/$setId/view",
        params: { setId: `${set.id}` },
      }).catch(console.error);
    },
  });

  if (!suggestions || suggestions.length === 0) return null;

  const widest = Math.max(...suggestions.map((s) => s.feed_count));

  return (
    <div className="rounded-lg border border-slate-6 dark:border-slatedark-6 bg-slate-subtle p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <PinIcon className="h-4 w-4 mt-1 shrink-0 text-slate-dim" />
        <div className="flex flex-col gap-1">
          <div className="font-medium">
            You already have {suggestions.length}{" "}
            {suggestions.length === 1 ? "post" : "posts"} spread across your
            feeds
          </div>
          <P className="text-sm">
            The widest is on {widest} feeds. Make them a set and you edit them
            in one place from now on. Your existing posts are kept exactly as
            they are &mdash; nothing is removed or re-added, so none of them
            lose their view counts.
          </P>
        </div>
      </div>
      <form
        className="flex flex-col sm:flex-row gap-2 sm:items-center"
        onSubmit={(e) => {
          e.preventDefault();
          const form = new FormData(e.target as HTMLFormElement);
          adopt.mutate((form.get("set_name") as string) || "My sticky posts");
        }}
      >
        <Input
          name="set_name"
          defaultValue="My sticky posts"
          aria-label="Name for the new set"
          className="sm:max-w-xs"
        />
        <Button type="submit" isLoading={adopt.isPending}>
          Make this a set
        </Button>
      </form>
      {adopt.error && (
        <div className="text-red-normal text-sm">{adopt.error.message}</div>
      )}
    </div>
  );
}

function ScopeBadge({ set }: { set: StickySetSummary }) {
  if (set.scope_mode === "tag") {
    return (
      <SimpleTooltip message="Applies to every feed carrying a tag, so new feeds join automatically.">
        <Badge variant="secondary">By tag</Badge>
      </SimpleTooltip>
    );
  }
  return <Badge variant="outline">Chosen feeds</Badge>;
}

function SetRow({ set }: { set: StickySetSummary }) {
  // Members and reach but nothing materialised means it has never been applied.
  const neverApplied =
    set.member_count > 0 && set.feed_count > 0 && set.materialised_rows === 0;

  return (
    <TableRow className="group cursor-pointer">
      <TableCell>
        <Link
          to="/app/sticky-sets/$setId/view"
          params={{ setId: `${set.id}` }}
          className="font-medium hover:underline"
        >
          {set.display_name}
        </Link>
        {set.description && (
          <div className="text-slate-dim text-sm">{set.description}</div>
        )}
      </TableCell>
      <TableCell>
        <ScopeBadge set={set} />
      </TableCell>
      <TableCell className="tabular-nums text-slate-dim">
        {set.member_count}
      </TableCell>
      <TableCell className="tabular-nums text-slate-dim">
        {set.feed_count}
      </TableCell>
      <TableCell>
        {!set.is_active ? (
          <Badge variant="secondary">Paused</Badge>
        ) : neverApplied ? (
          <SimpleTooltip message="This set has posts and feeds but hasn't been applied yet. Open it and choose Apply now.">
            <span className="flex items-center gap-1 text-sm text-yellow-normal">
              <AlertTriangleIcon className="h-3 w-3" />
              Not applied
            </span>
          </SimpleTooltip>
        ) : (
          <span className="text-slate-dim text-sm tabular-nums">
            {set.materialised_rows} live
          </span>
        )}
      </TableCell>
      <TableCell>
        <span className="flex gap-2 justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <div {...preventBubble}>
            <DeleteSetButton set={set} />
          </div>
        </span>
      </TableCell>
    </TableRow>
  );
}

function StickySetsTable() {
  const { data: sets } = useSuspenseQuery(getStickySetsQueryOptions());

  if (sets.length === 0) {
    return (
      <div className="flex flex-col gap-4 sm:items-start">
        <div className="text-slate-dim">
          You haven&apos;t made any sticky sets yet.
        </div>
        <ButtonLink to="/app/sticky-sets/create">Create a set</ButtonLink>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-6 dark:border-slatedark-6">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Applies to</TableHead>
            <TableHead className="whitespace-pre">Posts</TableHead>
            <TableHead className="whitespace-pre">Feeds</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>
              <span className="flex justify-end items-center">
                <span className="sr-only">Actions</span>
                <SimpleTooltip message="Create a sticky set">
                  <ButtonLink
                    variant="ghost"
                    size="smIcon"
                    to="/app/sticky-sets/create"
                  >
                    <span className="sr-only">Create a sticky set</span>
                    <PlusIcon />
                  </ButtonLink>
                </SimpleTooltip>
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sets.map((set) => (
            <SetRow key={set.id} set={set} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StickySetsPage() {
  return (
    <Page className="md:gap-10">
      <H1>Sticky Sets</H1>
      <div className="flex flex-col gap-6">
        <P>
          A sticky set is a group of pinned or rotating posts that runs across
          several of your feeds at once. Edit a post here and every feed in the
          set follows &mdash; and any individual feed can still turn one off or
          take a copy of its own.
        </P>
        <AdoptBanner />
        <StickySetsTable />
        <div className="flex items-center gap-2 text-slate-dim text-sm">
          <NewspaperIcon className="h-3 w-3" />
          To add feeds in bulk, select them on your feeds list and choose
          &ldquo;Add to sticky set&rdquo;.
        </div>
      </div>
    </Page>
  );
}
